/* eslint-disable indent */
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MultiBackend, TouchTransition } from 'react-dnd-multi-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import { Modal, SingleSelect } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { YesNoModal } from '@/components/modals/YesNoModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE, TEXT_OVERLAY } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getAssetFilterOptions, getSlotTypeTranslation } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useMobileScreen } from '@/hooks';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { CommandsModal } from '@/pages/Event/CeremonyTemplate//CommandsModal';
import { AddSlot } from '@/pages/Event/CeremonyTemplate/AddSlot';
import { AssetTypeFilter } from '@/pages/Event/CeremonyTemplate/AssetTypeFilter';
import { CeremonyTemplateSlot } from '@/pages/Event/CeremonyTemplate/CeremonyTemplateSlot';
import { TemplatePopup } from '@/pages/Event/CeremonyTemplate/TemplatePopup';
import { EVENTS_REFETCH_INTERVAL } from '@/pages/Events';
import {
  useDeleteAssetSlotMutation,
  useGetAllCeremonyTemplates,
  useGetAllDevices,
  useGetEventAssets,
  useGetEventByIdQuery,
  useUpdateAssetSlotMutation,
  useUpdateAssetSlotOrderMutation,
  useUpdateEventMutation
} from '@/services';
import {
  Asset,
  AssetsContainerProps,
  AssetSlot,
  AssetSlotEvent,
  AssetSlotUpdate,
  CeremonyTemplate,
  ErrorResponse,
  SelectOptionProps,
  ThemeProp
} from '@/types';
import { CeremonySlotType } from '@/types/CeremonyTemplateSlotTypes';
import { TestIds } from '@/shared/test-ids';

const DND_BACKENDS = {
  backends: [
    { backend: HTML5Backend },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition
    }
  ]
};

export const CeremonyTemplateView = ({
  eventId,
  organizationPermissions: eventOrganizationPermissions
}: AssetsContainerProps) => {
  const [t] = useTranslation('common');
  const isMobile = useMobileScreen();
  const [stopAssetSlotsChange, setStopAssetSlotsChange] = useState<boolean>(false);
  const [selectedCeremonyTemplate, setSelectedCeremonyTemplate] =
    useState<SelectOptionProps | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<SelectOptionProps | null>(null);
  const [assetSlots, setAssetSlots] = useState<AssetSlot[] | null>([]);
  const [selectedFilter, setSelectedFilter] = useState<SelectOptionProps | null>(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [slot, setSlot] = useState<AssetSlot | null>(null);
  const [isChangeContentStructureOpen, setIsChangeContentStructureOpen] = useState<boolean>(false);
  const [showEventUpdateMessage, setShowEventUpdateMessage] = useState<boolean>(false);
  const [isContentUploadConfirmed, setIsContentUploadConfirmed] = useState<boolean>(false);
  const [isAssetSelectOpen, setIsAssetSelectOpen] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<AssetSlot>();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isCommandsModalOpen, setIsModalCommandsOpen] = useState<boolean>(false);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState<boolean>(false);
  const [isReorderingEnabled, setIsReorderingEnabled] = useState<boolean>(false);

  const { organizationPermissions: userOrganizationPermissions, hasPermission } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const hasCeremonySetupAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const hasCreateEventTemplate = hasPermission(PERMISSION_TYPE.CREATE_EVENT_TEMPLATE);

  const {
    data: eventData,
    isLoading: isLoadingEventData,
    refetch: eventRefetch
  } = useGetEventByIdQuery(Number(eventId));

  const {
    isLoading: isLoadingCeremonyTemplates,
    data: allCeremonyTemplates,
    refetch: allCeremonyTemplatesRefetch
  } = useGetAllCeremonyTemplates(0, eventData?.data.organization.id, hasCeremonySetupAccess);

  const {
    data: allDevices,
    isLoading: isLoadingDevices,
    refetch: refetchAllDevices
  } = useGetAllDevices(
    PAGE_SIZE,
    '',
    undefined,
    undefined,
    undefined,
    eventData?.data.organization.id
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      !document.hidden && refetchAllDevices();
    }, EVENTS_REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [refetchAllDevices]);

  useEffect(() => {
    setIsContentUploadConfirmed(eventData?.data.isContentUploadConfirmed);
  }, [eventData?.data.isContentUploadConfirmed]);

  const { mutate: updateEvent } = useUpdateEventMutation(queryClient, {
    onSuccess: () => {
      if (showEventUpdateMessage) {
        showToast(ToastType.SUCCESS, t('eventUpdated'));
        setShowEventUpdateMessage(false);
      }
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutateAsync: updateAssetSlot } = useUpdateAssetSlotMutation(queryClient, {
    onSuccess: () => {
      setIsAssetSelectOpen(false);
      setSelectedAsset(null);
      eventRefetch();
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutate: deleteAssetSlot } = useDeleteAssetSlotMutation(queryClient, {
    onSuccess: () => {
      if (eventData?.data?.ceremonyTemplate?.id !== undefined) {
        setShowEventUpdateMessage(false);
        handleUpdateEvent(eventData?.data?.device?.id, null);
      }
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      showToast(ToastType.ERROR, t('slotDeleteError'));
    }
  });

  const { mutate: updateAssetSlotOrder } = useUpdateAssetSlotOrderMutation(queryClient, {
    onSuccess: () => {
      eventRefetch();
    },
    onError: (error: ErrorResponse) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  useEffect(() => {
    if (hasCeremonySetupAccess) {
      allCeremonyTemplatesRefetch();
    }
  }, [allCeremonyTemplatesRefetch]);

  const getTemplateOptions = () => {
    const templateOptions: SelectOptionProps[] =
      allCeremonyTemplates?.pages
        .flatMap((page) => page.data?.items)
        .map((ceremonyTemplate: CeremonyTemplate) => ({
          value: ceremonyTemplate.id,
          label: t(ceremonyTemplate.name)
        })) || [];

    const customOption: SelectOptionProps[] = [{ value: null, label: t('custom') }];
    const allOptions = customOption.concat(templateOptions);

    return allOptions;
  };

  const getDeviceOptions = () => {
    const deviceOptions: SelectOptionProps[] =
      allDevices?.pages
        .flatMap((page) => page.data?.items)
        .map((device) => ({
          value: device.id,
          label: device.name,
          tag: device.status,
          statusDescription: device.statusDescription
        })) || [];
    const notSelectedOption: SelectOptionProps[] = [{ value: null, label: t('notSelected') }];
    const allOptions = notSelectedOption.concat(deviceOptions);
    return allOptions;
  };

  const { data: allAssets, isLoading: isLoadingAllAssets } = useGetEventAssets(
    eventId as string,
    [],
    0,
    '',
    [selectedFilter?.value] as string[],
    undefined,
    undefined,
    hasContentManagementAccess
  );

  const handleUpdateEvent = (
    deviceId: string | undefined | null,
    ceremonyTemplateId: string | undefined | null,
    isContentUploadConfirmed?: boolean | undefined | null,
    isNotifyingUploaders?: boolean | undefined | null
  ) => {
    updateEvent({
      ...eventData?.data,
      organizationId: eventData?.data?.organization?.id,
      deviceId: deviceId,
      ceremonyTemplateId: ceremonyTemplateId,
      isContentUploadConfirmed: isContentUploadConfirmed,
      isNotifyingUploaders: isNotifyingUploaders
    });
  };

  const handleTemplateChange = (newValue: SingleValue<SelectOptionProps>) => {
    setStopAssetSlotsChange(false);

    const template = newValue as SelectOptionProps;

    setSelectedCeremonyTemplate({
      value: template?.value as string | number | boolean | null,
      label: t(template?.label as string)
    });
    if (isContentUploadConfirmed) {
      setIsChangeContentStructureOpen(true);
    } else {
      handleUpdateEvent(eventData?.data?.device?.id, template?.value?.toString());
    }
  };

  const handleDeviceChange = (newValue: SingleValue<SelectOptionProps>) => {
    const device: SelectOptionProps = newValue as SelectOptionProps;
    setSelectedDevice(device);
    handleUpdateEvent(device?.value?.toString(), eventData?.data?.ceremonyTemplate?.id);
  };

  const mapCeremonyTemplateSlotsToAssetSlots = (currentAssetSlots: AssetSlot[]): AssetSlot[] => {
    const allTemplates: CeremonyTemplate[] =
      allCeremonyTemplates?.pages.flatMap((page) => page.data?.items) ?? [];

    const selectedTemplateDetails = allTemplates.find(
      (ceremonyTemplate) => ceremonyTemplate.id === selectedCeremonyTemplate?.value
    );

    const updatedSlots: AssetSlot[] = currentAssetSlots.map<AssetSlot>((assetSlot: AssetSlot) => {
      const matchingTemplateSlot = selectedTemplateDetails?.slots.find(
        (templateSlot) => templateSlot.order === assetSlot.order
      );

      if (matchingTemplateSlot) {
        const newAssetSlotEvents: AssetSlotEvent[] =
          matchingTemplateSlot.ceremonyTemplateSlotEvents.map<AssetSlotEvent>((templateEvent) => ({
            id: templateEvent.id,
            assetSlotId: assetSlot.id,
            assetSlotTrigger: templateEvent.assetSlotTrigger,
            assetSlotHandler: templateEvent.assetSlotHandler,
            value: String(
              currentAssetSlots.find(
                (assetSlot: AssetSlot) => assetSlot.order === Number(templateEvent.value)
              )?.id
            )
          }));

        return {
          ...assetSlot,
          assetSlotEvents: newAssetSlotEvents
        };
      }

      return assetSlot;
    });
    return updatedSlots;
  };

  useEffect(() => {
    if (eventData?.data.assetSlots) {
      setAssetSlots(mapCeremonyTemplateSlotsToAssetSlots(eventData?.data.assetSlots) || []);
    }
  }, [eventData?.data.assetSlots, selectedCeremonyTemplate]);

  useEffect(() => {
    if (eventData?.data) {
      setSelectedCeremonyTemplate({
        value: eventData?.data?.ceremonyTemplate?.id,
        label: t(eventData?.data?.ceremonyTemplate?.name || 'custom')
      });
      setSelectedDevice({
        value: eventData?.data?.device?.id,
        label: eventData?.data?.device?.name || t('notSelected'),
        tag: eventData?.data?.device?.status,
        statusDescription: eventData?.data?.device?.statusDescription
      });
    }
  }, [eventData]);

  const handleFilterClick = (value: SelectOptionProps) => {
    if (selectedFilter?.value === value?.value) {
      setSelectedFilter(null);
    } else {
      setSelectedFilter(value);
    }
  };

  const handleUpdateAssetOrder = async (currentOrder: number, targetedOrder: number) => {
    if (currentOrder === targetedOrder) {
      return;
    }

    const minOrder = Math.min(currentOrder, targetedOrder);
    const maxOrder = Math.max(currentOrder, targetedOrder);

    const getNewOrder = (slotOrder: number) => {
      if (slotOrder === currentOrder) {
        return targetedOrder;
      }
      return currentOrder < targetedOrder ? slotOrder - 1 : slotOrder + 1;
    };

    const payload = assetSlots
      ?.filter((slot) => slot.order >= minOrder && slot.order <= maxOrder)
      .map((slot) => ({
        id: slot.id as number,
        order: getNewOrder(slot.order)
      }));

    if (payload?.length) {
      setAssetSlots(
        (prevSlots: AssetSlot[] | null) =>
          prevSlots
            ?.map((slot: AssetSlot) => {
              const updatedSlot = payload.find((p) => p.id === slot.id);
              return updatedSlot ? { ...slot, order: updatedSlot.order } : slot;
            })
            ?.sort((a: AssetSlot, b: AssetSlot) => a.order - b.order) ?? []
      );

      await updateAssetSlotOrder(payload);
    }

    if (eventData?.data.ceremonyTemplate) {
      handleUpdateEvent(selectedDevice?.value?.toString(), null);
    }
  };

  const handleDeleteSlot = (assetSlot: AssetSlot) => {
    setSlot(assetSlot);
    setIsOpenDeleteModal(true);
  };

  const handleSlotClick = (slot: AssetSlot) => {
    if (!slot || !slot.slotType) {
      return;
    }
    if (isMobile) {
      setIsActionsModalOpen(true);
      setSelectedFilter({ value: slot.slotType.id - 1, label: slot.slotType.assetType.toString() });
      setSlot(slot);
    } else {
      setIsModalCommandsOpen(true);
    }

    setSelectedSlot(slot);
  };

  const saveSlotAsset = () => {
    if (selectedAsset && selectedSlot) {
      const slot = {
        ...selectedSlot,
        assetId: Number(selectedAsset?.id)
      };
      updateAssetSlot(slot as AssetSlotUpdate);
    }
  };

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleCancelClick = () => {
    setIsAssetSelectOpen(false);
    setSelectedAsset(null);
  };

  return (
    <div
      className={`no-scrollbar mb-10 size-full overflow-y-auto ${hasCeremonySetupAccess && hasContentManagementAccess ? '' : 'relative'}`}>
      {isLoadingDevices ||
      (hasCeremonySetupAccess && isLoadingCeremonyTemplates) ||
      isLoadingEventData ||
      (hasContentManagementAccess && isLoadingAllAssets) ? (
        <Spinner />
      ) : isAssetSelectOpen ? (
        <div className='no-scrollbar size-full flex-col items-center overflow-y-auto rounded-lg py-4 md:hidden'>
          <Label type={LABEL_TYPE.H3_BOLD} text={t('selectAsset')} />
          <div className='mb-5 mt-4 grid h-[85%] grid-cols-2 gap-6 overflow-y-scroll px-6'>
            {allAssets?.pages.flatMap((page) => page.data?.items).length !== 0}
            {allAssets?.pages
              .flatMap((page) => page.data?.items)
              .filter((asset: Asset) => asset.assetType !== TEXT_OVERLAY)
              .map((asset: Asset) => (
                <AssetItem
                  asset={asset}
                  key={asset.id}
                  assetType={asset.assetType}
                  canBeDeleted={false}
                  canBeDragged
                  setSelectedAsset={
                    setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
                  }
                  selectedVideoUrl={''}
                />
              ))}
          </div>
          <div className='flex w-full flex-row gap-2'>
            <Button
              variant={ButtonVariant.SECONDARY}
              className='w-1/2'
              handleOnClick={handleCancelClick}>
              {t('cancel')}
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              className='w-1/2'
              disabled={selectedAsset === null}
              handleOnClick={saveSlotAsset}>
              {t('save')}
            </Button>
          </div>
        </div>
      ) : (
        <DndProvider backend={MultiBackend} options={DND_BACKENDS}>
          <div className='grid size-full grid-cols-1 gap-x-14 pt-2 md:grid-cols-2 md:px-20 md:pt-9'>
            <div className='no-scrollbar mb-1 hidden size-full flex-col items-center overflow-y-auto rounded-lg bg-color-light-blue md:flex'>
              <div className='grid w-full grid-cols-2 gap-4 px-6 py-9'>
                {getAssetFilterOptions(organizationPermissions).map((item) => (
                  <AssetTypeFilter
                    title={item.label}
                    key={item.value}
                    onClick={() => handleFilterClick(item)}
                    isClicked={item.value === selectedFilter?.value}
                  />
                ))}
              </div>
              <div className='mb-5 grid grid-cols-3 gap-6 px-6'>
                {allAssets?.pages
                  .flatMap((page) => page.data.items)
                  .map((asset: Asset, index: number) => (
                    <AssetItem
                      asset={asset}
                      key={asset.id}
                      assetType={asset.assetType}
                      canBeDeleted={false}
                      canBeDragged
                      selectedVideoUrl={''}
                      dataTestId={`${TestIds.eventCeremony.assetItemCard}-${index}`}
                    />
                  ))}
              </div>
            </div>
            <div className='no-scrollbar h-full gap-y-9 overflow-y-auto'>
              <div className='flex flex-col justify-center gap-y-6 rounded-lg p-2 md:border md:p-6'>
                <div data-testid={TestIds.eventCeremony.deviceSelect}>
                  <SingleSelect
                    isClearable={false}
                    id='device-select'
                    label={t('device')}
                    isRequired
                    disabled={isLoadingCeremonyTemplates}
                    options={getDeviceOptions()}
                    handleChange={(value) => {
                      handleDeviceChange(value);
                    }}
                    value={selectedDevice}
                    errorMessage={String()}
                  />
                </div>
                <div data-testid={TestIds.eventCeremony.templateSelect}>
                  <SingleSelect
                    isClearable={false}
                    id='template-select'
                    label={t('contentStructureTemplate')}
                    isRequired
                    disabled={isLoadingCeremonyTemplates}
                    options={getTemplateOptions()}
                    handleChange={(value) => {
                      handleTemplateChange(value);
                    }}
                    value={selectedCeremonyTemplate}
                    errorMessage={String()}
                  />
                </div>
              </div>
              <div
                className={`mb:pt-9 mb-10 flex w-full flex-wrap ${isReorderingEnabled ? 'rounded-2xl border-2 border-accent-light-blue pt-1 md:border-0' : 'pt-7'}`}
                data-testid={TestIds.eventCeremony.slotList}>
                {isReorderingEnabled && isMobile && (
                  <p className='pl-3 text-xs text-secondary-gray'>Reordering</p>
                )}
                <div className='flex w-full flex-wrap'>
                  {assetSlots?.map((slot, index) => (
                    <CeremonyTemplateSlot
                      type={slot.slotType as CeremonySlotType}
                      key={slot.id}
                      slot={slot as AssetSlot}
                      handleUpdateAssetOrder={handleUpdateAssetOrder}
                      deleteCeremonyTemplateSlot={handleDeleteSlot as (asset: AssetSlot) => void}
                      refetchEvent={eventRefetch}
                      onClick={() => handleSlotClick(slot)}
                      isReorderingEnabled={isReorderingEnabled}
                      setIsReorderingEnabled={setIsReorderingEnabled}
                      dataTestId={`${TestIds.eventCeremony.slotItem}-${index}`}
                    />
                  ))}
                  <div className='w-1/2 p-2 md:w-1/4'>
                    <AddSlot
                      eventData={eventData?.data}
                      numberOfSlots={assetSlots?.length ?? 0}
                      hasCeremonySetupAccess={hasCeremonySetupAccess}
                      handleTemplateChange={function (): void {
                        throw new Error('Function not implemented.');
                      }}
                    />
                  </div>
                  {isOpenDeleteModal &&
                    (isContentUploadConfirmed ? (
                      <YesNoModal
                        onConfirm={() => {
                          deleteAssetSlot(Number(slot?.id));
                          handleUpdateEvent(eventData?.data?.device?.id, null, false, true);
                        }}
                        isOpen={isOpenDeleteModal}
                        onClose={() => setIsOpenDeleteModal(false)}
                        onDeny={() => {
                          deleteAssetSlot(Number(slot?.id));
                          handleUpdateEvent(eventData?.data?.device?.id, null, false);
                        }}
                        message={t('deleteSlotMessage', {
                          item: getSlotTypeTranslation(slot?.slotTypeId as number)
                        })}
                      />
                    ) : (
                      <ConfirmationModal
                        onConfirm={() => {
                          deleteAssetSlot(Number(slot?.id));
                          handleUpdateEvent(eventData?.data?.device?.id, null, false);
                        }}
                        isOpen={isOpenDeleteModal}
                        onClose={() => setIsOpenDeleteModal(false)}
                        message={t('deleteMessage', {
                          item: getSlotTypeTranslation(slot?.slotTypeId as number)
                        })}
                      />
                    ))}
                  {isChangeContentStructureOpen && isContentUploadConfirmed && (
                    <AddSlot
                      eventData={eventData?.data}
                      numberOfSlots={assetSlots?.length ?? 0}
                      isOpenNotifyingUploadersModal={true}
                      isTemplateChange={true}
                      hasCeremonySetupAccess={hasCeremonySetupAccess}
                      handleTemplateChange={(value) => {
                        handleUpdateEvent(
                          eventData?.data?.device?.id,
                          selectedCeremonyTemplate?.value?.toString(),
                          false,
                          value
                        );
                        setIsChangeContentStructureOpen(false);
                      }}
                    />
                  )}
                  {isCommandsModalOpen && (
                    <CommandsModal
                      assetSlots={assetSlots as AssetSlot[]}
                      updateAssetSlots={setAssetSlots}
                      stopAssetSlotsChange={setStopAssetSlotsChange}
                      selectedSlot={selectedSlot as AssetSlot}
                      isModalOpen={isCommandsModalOpen}
                      setIsModalOpen={setIsModalCommandsOpen}
                      refetchEvent={eventRefetch}
                    />
                  )}
                  {isActionsModalOpen && (
                    <Modal
                      isOpen={isActionsModalOpen}
                      onClose={() => {
                        setIsActionsModalOpen(false);
                      }}
                      height='400px'
                      modalClasses='w-full md:w-[490px]'
                      label={t('actions')}
                      labelClasses='text-2xl font-bold text-primary-blue pl-2'>
                      <div className='flex flex-col items-center gap-2'>
                        <Button
                          className='w-full'
                          variant={ButtonVariant.SECONDARY}
                          handleOnClick={() => {
                            setIsAssetSelectOpen(true);
                            setIsActionsModalOpen(false);
                          }}>
                          {t('openAssetSelection')}
                        </Button>
                        <Button
                          className='w-full'
                          variant={ButtonVariant.SECONDARY}
                          handleOnClick={() => {
                            setIsModalCommandsOpen(true);
                            setIsActionsModalOpen(false);
                          }}>
                          {t('openSlotCommands')}
                        </Button>
                      </div>
                    </Modal>
                  )}
                </div>
              </div>
              {!eventData?.data.ceremonyTemplate && hasCreateEventTemplate && (
                <div className='mb-5 flex w-full justify-end'>
                  <Button
                    type='submit'
                    className='w-full md:w-fit'
                    variant={ButtonVariant.PRIMARY}
                    disabled={assetSlots?.length === 0}
                    handleOnClick={handleOpenPopup}>
                    {t('saveTemplate')}
                  </Button>
                </div>
              )}
              {isPopupOpen && (
                <TemplatePopup
                  onClose={handleClosePopup}
                  onSave={() => allCeremonyTemplatesRefetch()}
                  organization={eventData?.data.organization}
                  assets={assetSlots ? assetSlots : []}
                  eventId={eventData?.data?.id}
                  deviceId={eventData?.data?.device?.id ?? null}
                />
              )}
            </div>
          </div>
        </DndProvider>
      )}
      {(!hasCeremonySetupAccess || !hasContentManagementAccess) && (
        <div className='absolute inset-0 z-10 backdrop-blur-sm'>
          <Modal
            isOpen={!hasCeremonySetupAccess || !hasContentManagementAccess}
            modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
            hasCloseIcon={false}
            onClose={() => {
              undefined;
            }}>
            <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
              <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
              <Label
                type={LABEL_TYPE.PARAGRAPH2}
                text={t('yourOranizationDoesntHavePermission', {
                  permission: hasCeremonySetupAccess
                    ? hasContentManagementAccess
                      ? ''
                      : t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`)
                    : t(`${ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP}`)
                })}
              />
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};
